import { useHasPurchasedLifetimeCourses } from '@/hooks/usePurchasedLifetimeCourses';
import { useUserSubscriptionState } from '@/hooks/useUserSubscriptionState';
import { useAuth } from '@/lib/AuthContext';
import { PRO_PROMO_FALL26 } from '@/lib/constants';
import { readMembershipSource } from '@/lib/membershipSource';
import {
  isNativeProPromoAudience,
  isNativeProPromoWindow,
  isProPromoAudience,
  isProPromoCountdownPhase,
  isProPromoPreviewUser,
  isProPromoWindow,
} from '@/lib/proPromoBanner';
import { getCustomerInfo, hasActiveEntitlement } from '@/lib/purchases/RevenueCat';
import { useAppleTrial } from '@/lib/purchases/useAppleTrial';
import { isSubscriptionRowOnTrial } from '@/lib/trialCourseCta';
import { openWpUrlInBrowserLoggedIn } from '@/lib/wpBrowserAutologin';
import { getSubscriptionInterval } from '@/services/subscription-interval.service';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

const SUBSCRIPTION_INTERVAL_STALE_MS = 30 * 60 * 1000; // membership interval changes rarely
const WINDOW_TICK_MS = 60 * 1000; // re-check the live window every minute so it flips without a reload

// Closing the banner hides it for good on that browser / device (management
// rule: "let's not show it again if they close it"). Keyed per user so a
// shared computer doesn't hide it for the next member who logs in.
const DISMISSED_STORAGE_PREFIX = 'pro-promo-banner-fall26-dismissed:';

const IS_WEB = Platform.OS === 'web';

export interface ProPromoBanner {
  /** Open the offer: the WP promo page on web, the right in-app path on iOS. */
  open: () => void;
  /** Final days: render the countdown clock in place of the "Limited Time" pill. */
  countdown: boolean;
  /** Hide the banner permanently on this browser / device. */
  dismiss: () => void;
}

function storageKey(userId: string): string {
  return `${DISMISSED_STORAGE_PREFIX}${userId}`;
}

async function readDismissed(userId: string | undefined): Promise<boolean> {
  if (!userId) return false;
  try {
    if (IS_WEB) {
      if (typeof window === 'undefined') return false;
      return window.localStorage.getItem(storageKey(userId)) === '1';
    }
    return (await AsyncStorage.getItem(storageKey(userId))) === '1';
  } catch {
    return false;
  }
}

async function writeDismissed(userId: string): Promise<void> {
  try {
    if (IS_WEB) {
      if (typeof window !== 'undefined') window.localStorage.setItem(storageKey(userId), '1');
      return;
    }
    await AsyncStorage.setItem(storageKey(userId), '1');
  } catch {
    // Storage blocked (private mode etc.): dismissal just lasts the session.
  }
}

function openPromoUrl(url: string) {
  if (IS_WEB && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    void Linking.openURL(url);
  }
}

/**
 * Resolves the Fall '26 OS Pro promo banner for the current (real, logged-in)
 * user, or `null` when it should not show.
 *
 * Web — same rules as the WP site banner (Kadence conversion 1632698 +
 * osj-pro-promo-banner-fall26.php):
 *   - live Sept 28 – Oct 4 2026 (Chicago time)
 *   - OS Regular members (any interval) and OS Pro monthly members only
 *   - never Pro annual, Lifetime, or non-members
 *   - opens the WP /pro-special1/ page
 *
 * iOS — promotes the App Store price cut on OS Pro annual (Dan, 2026-09-28):
 *   - live Sept 30 – Oct 4 2026 (the dates Apple allowed for the price change)
 *   - not on trial (WooCommerce trial row or Apple intro trial)
 *   - excludes OS Pro members; includes Regular members and individual course
 *     purchasers; free accounts with nothing purchased get nothing
 *   - tap routes by billing, like the Hang's pro-upgrade intercept: device owns
 *     the Apple sub → native Pro upgrade; Apple-billed on another Apple ID →
 *     Membership (Restore); non-member course purchaser → paywall; web-billed
 *     member → the WP promo page in the browser, logged in
 *
 * Once closed it never comes back on that browser / device. Reviewer accounts
 * listed in PRO_PROMO_FALL26.PREVIEW_EMAILS bypass the audience rules, and on
 * staging also the live window; their dismissal is not persisted.
 *
 * The subscription-interval fetch (web) is only enabled while the banner could
 * show, so there's no WordPress load outside the window.
 */
export function useProPromoBanner(): ProPromoBanner | null {
  const { user, loading, membershipTier, membershipLoading, preferenceMetadata, preferencesLoading } =
    useAuth();
  const router = useRouter();
  const isRealUser = !!user && !user.is_anonymous;
  const isPreviewUser = isRealUser && isProPromoPreviewUser(user?.email);
  const userId = user?.id;

  const [withinWindow, setWithinWindow] = useState(() =>
    IS_WEB ? isProPromoWindow(Date.now()) : isNativeProPromoWindow(Date.now()),
  );
  const [countdown, setCountdown] = useState(() => isProPromoCountdownPhase(Date.now()));
  // null = not read yet for this user; never show before the flag is known.
  const [dismissed, setDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    const tick = () => {
      setWithinWindow(IS_WEB ? isProPromoWindow(Date.now()) : isNativeProPromoWindow(Date.now()));
      setCountdown(isProPromoCountdownPhase(Date.now())); // clock swaps in without a reload
    };
    tick();
    const id = setInterval(tick, WINDOW_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Re-read the per-user flag when the signed-in user changes.
  useEffect(() => {
    let cancelled = false;
    setDismissed(null);
    void readDismissed(userId).then((value) => {
      if (!cancelled) setDismissed(value);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const dismiss = useCallback(() => {
    if (!isPreviewUser && userId) void writeDismissed(userId);
    setDismissed(true);
  }, [isPreviewUser, userId]);

  const bypassWindow = isPreviewUser && PRO_PROMO_FALL26.PREVIEW_BYPASSES_WINDOW;
  const couldShow = isRealUser && dismissed === false && (withinWindow || bypassWindow);
  // Staging previewers see the final-days clock without waiting for its switch-on time.
  const showCountdown = countdown || bypassWindow;

  // Web audience: interval + lifetime from the WP-backed edge function.
  // `data` is `undefined` until the query resolves; we wait for isSuccess so we
  // never flash the banner at a Pro annual member before we know their interval.
  const { data: interval, isSuccess: intervalResolved } = useQuery({
    queryKey: ['subscription-interval', userId],
    queryFn: getSubscriptionInterval,
    enabled: IS_WEB && !loading && couldShow && !isPreviewUser,
    staleTime: SUBSCRIPTION_INTERVAL_STALE_MS,
  });

  // Native audience: trial state (WooCommerce row + Apple entitlement) and
  // individual course purchases. Both hooks are cheap no-ops when disabled.
  const nativeChecks = !IS_WEB && couldShow && !isPreviewUser;
  const { data: subscriptionRow = null, isSuccess: subscriptionResolved } = useUserSubscriptionState({
    userId,
    enabled: nativeChecks,
  });
  const appleTrial = useAppleTrial(nativeChecks ? userId : undefined);
  const { hasPurchasedLifetimeCourses, isLoading: purchasesLoading } = useHasPurchasedLifetimeCourses();

  const openWeb = useCallback(() => openPromoUrl(PRO_PROMO_FALL26.URL), []);

  const openNative = useCallback(async () => {
    const info = await getCustomerInfo();
    if (hasActiveEntitlement(info)) {
      router.push('/upgrade?mode=pro');
      return;
    }
    if (readMembershipSource(preferenceMetadata) === 'apple') {
      router.push('/membership');
      return;
    }
    if (membershipTier === 'non_member') {
      router.push('/upgrade');
      return;
    }
    // Web-billed member: Apple can't switch a WooCommerce subscription, so the
    // offer lives on the web — hand off to the browser, logged in.
    void openWpUrlInBrowserLoggedIn(PRO_PROMO_FALL26.URL, '/pro-special1/');
  }, [membershipTier, preferenceMetadata, router]);

  const open = IS_WEB ? openWeb : () => void openNative();

  if (!couldShow) return null;

  if (isPreviewUser) return { open, dismiss, countdown: showCountdown };

  if (membershipLoading) return null;

  if (IS_WEB) {
    if (!intervalResolved) return null;
    const eligible = isProPromoAudience({
      tier: membershipTier,
      interval: interval.interval,
      hasLifetime: interval.hasLifetime,
    });
    return eligible ? { open, dismiss, countdown: showCountdown } : null;
  }

  if (!subscriptionResolved || !appleTrial.resolved || preferencesLoading || purchasesLoading) {
    return null;
  }
  const eligible = isNativeProPromoAudience({
    tier: membershipTier,
    onTrial: isSubscriptionRowOnTrial(subscriptionRow, user?.created_at) || appleTrial.onTrial,
    hasPurchasedCourses: hasPurchasedLifetimeCourses,
  });
  return eligible ? { open, dismiss, countdown: showCountdown } : null;
}
